import React from "react";
import { Box, Option, Tooltip } from "@mui/joy";
import { SBackground, SUserAdd } from "styles/StyledMUI";
import { Permission, usePermissions } from "contexts/app/permissions";
import { useUsersModuleData } from "contexts/app/usersModuleData";
import UserInviteComponent from "./display/UserInviteComponent";
import UserItem from "./display/UserItem";
import IconButtonCTA from "components/common/IconButtonCTA";
import DarkSelect from "components/common/DarkSelect";
import DarkTypography from "components/common/DarkTypography";
import { useUserListRouteIntent } from "./hooks/useUserListRouteIntent";
import { useUserListRoleFilter } from "./hooks/useUserListRoleFilter";
import { colorTokens } from "styles/globalStyles";

export default function UserListPage() {
  const { users, refreshUsers, qualificationItems, permissionsCatalog } = useUsersModuleData();
  const { hasPermission } = usePermissions();
  const { targetUserId, openUserDocuments } = useUserListRouteIntent();
  const { roleFilterKey, setRoleFilterKey, accountFilterKey, setAccountFilterKey, filteredUsers } =
    useUserListRoleFilter(users);
  const canInvite =
    hasPermission(Permission.USERS_ALL_EDIT) || hasPermission(Permission.USERS_COMPANY_EDIT);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const roleFilterOptions = React.useMemo(() => {
    const catalog = permissionsCatalog?.roles ?? [];
    const byId = new Map<number, string>();
    for (const r of catalog) {
      byId.set(Number(r.id), r.name);
    }
    users?.forEach((u) => {
      const id = u.role?.id;
      if (id == null || Number(id) <= 0) return;
      const numId = Number(id);
      if (!byId.has(numId)) {
        byId.set(numId, u.role?.name?.trim() ? u.role.name : `Rôle #${numId}`);
      }
    });
    return Array.from(byId.entries()).sort((a, b) => a[1].localeCompare(b[1], "fr"));
  }, [permissionsCatalog?.roles, users]);

  const hasUsers = Boolean(users?.length);
  const isFilteredEmpty = hasUsers && filteredUsers.length === 0;

  return (
    <Box sx={{ width: "100%", padding: 2, height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          marginBottom: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            flex: 1,
            minWidth: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <DarkSelect
            size="sm"
            value={accountFilterKey}
            onChange={(_, newValue) =>
              setAccountFilterKey((newValue as typeof accountFilterKey) ?? "all")
            }
            placeholder="Compte"
            aria-label="Filtrer par type de compte"
            disabled={!hasUsers}
            sx={{
              minWidth: { xs: "100%", sm: 180 },
              maxWidth: { sm: 220 },
              flex: { sm: "0 1 auto" },
            }}
          >
            <Option value="all">Tous</Option>
            <Option value="with_account">Avec compte</Option>
            <Option value="without_account">Sans compte</Option>
          </DarkSelect>
          <DarkSelect
            size="sm"
            value={roleFilterKey}
            onChange={(_, newValue) => setRoleFilterKey(String(newValue ?? "all"))}
            placeholder="Rôle"
            aria-label="Filtrer par rôle"
            disabled={!hasUsers}
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { sm: 360 },
              flex: { sm: "0 1 auto" },
            }}
          >
            <Option value="all">Tous les rôles</Option>
            <Option value="none">Sans rôle</Option>
            {roleFilterOptions.map(([id, name]) => (
              <Option key={id} value={String(id)} label={name}>
                {name}
              </Option>
            ))}
          </DarkSelect>
        </Box>

        {canInvite ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-end", sm: "flex-end" },
              flexShrink: 0,
            }}
          >
            <Tooltip title="Ajouter un utilisateur">
              <span>
                <IconButtonCTA
                  aria-label="Ajouter un utilisateur"
                  ctaVariant="accent"
                  onClick={() => setIsOpen(true)}
                  variant="plain"
                >
                  <SUserAdd />
                </IconButtonCTA>
              </span>
            </Tooltip>
          </Box>
        ) : null}
      </Box>

      {isFilteredEmpty ? (
        <Box
          sx={{
            py: 3,
            px: 2,
            borderRadius: "8px",
            border: `1px solid ${colorTokens.border.default}`,
            backgroundColor: colorTokens.bg.surfaceMuted,
            textAlign: "center",
          }}
        >
          <DarkTypography level="body-md" colorVariant="muted">
            Aucun utilisateur pour ces filtres.
          </DarkTypography>
        </Box>
      ) : null}

      {filteredUsers.map((u) => (
        <UserItem
          key={u.user.id}
          userWithRole={u}
          refreshUsers={refreshUsers}
          qualificationItems={qualificationItems}
          autoOpenDocuments={openUserDocuments && targetUserId === u.user.id}
        />
      ))}

      {canInvite && isOpen && (
        <SBackground>
          <UserInviteComponent setIsOpen={setIsOpen} onCreated={refreshUsers} />
        </SBackground>
      )}
    </Box>
  );
}
